// src/features/home/components/MyTasksTable.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Typography, Link as MuiLink,
} from '@mui/material';
import { Assignment as AssignmentIcon } from '@mui/icons-material';
import TaskRow from './TaskRow';
import HomeCard from './HomeCard';

function dedupeTasks(tasks) {
  const byId = new Map();
  for (const task of tasks) {
    const existing = byId.get(task.task_id);
    if (existing) existing.count += 1;
    else byId.set(task.task_id, { task, count: 1 });
  }
  return [...byId.values()];
}

export default function MyTasksTable({ tasks, loading }) {
  const { t } = useTranslation(['home', 'tasks']);
  const navigate = useNavigate();

  const deduped = useMemo(() => dedupeTasks(tasks || []), [tasks]);

  return (
    <HomeCard
      large
      icon={<AssignmentIcon sx={{ fontSize: 22 }} />}
      title={t('home:my_tasks.title')}
      action={(
        <MuiLink
          component="button"
          variant="body2"
          underline="hover"
          fontWeight={600}
          onClick={() => navigate('/tasks?filter=my-tasks')}
        >
          {t('home:my_tasks.view_all')}
        </MuiLink>
      )}
    >
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : deduped.length === 0 ? (
        <Typography variant="body2" color="text.tertiary" fontStyle="italic" sx={{ py: 2 }}>
          {t('home:my_tasks.empty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }}>{t('tasks:data_grid.columns.deadline')}</TableCell>
              <TableCell>{t('tasks:data_grid.columns.title')}</TableCell>
              <TableCell>{t('tasks:data_grid.columns.due')}</TableCell>
              <TableCell>{t('tasks:data_grid.columns.status')}</TableCell>
              <TableCell sx={{ pr: 0, width: 32 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {deduped.map(({ task, count }) => (
              <TaskRow
                key={task.task_id}
                task={task}
                badge={count > 1 && (
                  <Chip
                    label={`×${count}`}
                    size="small"
                    variant="outlined"
                    sx={{ color: 'text.disabled', borderColor: 'divider' }}
                  />
                )}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </HomeCard>
  );
}
